import { useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import axios from "axios";
import {
  Smartphone,
  Copy,
  Play,
  CheckCircle2,
  XCircle,
  Loader2,
  Terminal,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const CHECKS = [
  {
    id: "root",
    label: "GET /api/",
    desc: "Root health probe (used by mobile admin screen).",
    run: async () => {
      const r = await axios.get(`${API}/`);
      return { ok: !!r.data, note: r.data?.message || "" };
    },
  },
  {
    id: "categories",
    label: "GET /api/categories",
    desc: "Category list (mobile home screen drawer).",
    run: async () => {
      const r = await axios.get(`${API}/categories`);
      return { ok: Array.isArray(r.data), note: `${r.data.length} categories` };
    },
  },
  {
    id: "ad-config",
    label: "GET /api/ad-config",
    desc: "Ad configuration (public — used by AdBanner + VideoAdModal).",
    run: async () => {
      const r = await axios.get(`${API}/ad-config`);
      return {
        ok: !!r.data?.placements,
        note: `${Object.keys(r.data.placements || {}).length} placements, ${
          r.data.video_ad?.rules?.length || 0
        } video rules`,
      };
    },
  },
  {
    id: "nearby",
    label: "POST /api/places/nearby",
    desc: "Nearby search — requires Google Maps API key.",
    run: async () => {
      try {
        const r = await axios.post(`${API}/places/nearby`, {
          latitude: 37.7749,
          longitude: -122.4194,
          included_types: ["restaurant"],
          radius: 2000,
          max_results: 3,
        });
        return {
          ok: Array.isArray(r.data),
          note: `${r.data.length} places returned`,
        };
      } catch (e) {
        const detail =
          e?.response?.data?.detail ||
          e?.response?.statusText ||
          e?.message ||
          "failed";
        return {
          ok: false,
          note: typeof detail === "string" ? detail : JSON.stringify(detail),
        };
      }
    },
  },
  {
    id: "analytics",
    label: "POST /api/analytics/track",
    desc: "Ad impression tracker (mobile posts here on every ad shown).",
    run: async () => {
      const r = await axios.post(`${API}/analytics/track`, {
        event: "impression",
        rule_id: "connectivity_test",
        category: "test",
      });
      return { ok: r.data?.status === "ok", note: "impression tracked" };
    },
  },
];

export default function MobileConnection() {
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);

  const runAll = async () => {
    setRunning(true);
    setResults({});
    for (const c of CHECKS) {
      setResults((prev) => ({ ...prev, [c.id]: { status: "running" } }));
      try {
        const out = await c.run();
        setResults((prev) => ({
          ...prev,
          [c.id]: { status: out.ok ? "pass" : "fail", note: out.note },
        }));
      } catch (e) {
        setResults((prev) => ({
          ...prev,
          [c.id]: { status: "fail", note: e?.message || "error" },
        }));
      }
    }
    setRunning(false);
    toast.success("Connectivity checks complete");
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const backendOrigin = API.replace(/\/api$/, "");
  const envSnippet = `EXPO_PUBLIC_BACKEND_URL=${backendOrigin}`;

  return (
    <div className="p-6 lg:p-10 max-w-4xl">
      <div className="mb-8 animate-in-up">
        <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
          Integration
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
          Mobile App Connection
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Point the NearMe mobile app at this backend and verify every endpoint
          the app depends on.
        </p>
      </div>

      {/* Setup */}
      <section className="bg-card border border-border rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-accent text-accent-foreground flex items-center justify-center">
            <Smartphone className="w-5 h-5" />
          </div>
          <div>
            <div className="text-base font-bold">1. Configure the mobile app</div>
            <div className="text-xs text-muted-foreground">
              Add this to <span className="mono">frontend/.env</span> of the
              NearMe Expo project.
            </div>
          </div>
        </div>
        <div
          className="mono text-sm bg-secondary text-secondary-foreground rounded-lg p-4 flex items-center justify-between gap-3"
          data-testid="env-snippet"
        >
          <span className="break-all">{envSnippet}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => copy(envSnippet)}
            data-testid="copy-env-btn"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
          </Button>
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
          After changing it, run{" "}
          <span className="mono px-1.5 py-0.5 rounded bg-secondary">
            npx expo start -c
          </span>{" "}
          to clear the Metro cache.
        </div>
      </section>

      {/* Connectivity tests */}
      <section className="bg-card border border-border rounded-2xl p-6">
        <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent text-accent-foreground flex items-center justify-center">
              <Terminal className="w-5 h-5" />
            </div>
            <div>
              <div className="text-base font-bold">2. Run connectivity tests</div>
              <div className="text-xs text-muted-foreground">
                Hits every public endpoint the mobile app calls.
              </div>
            </div>
          </div>
          <Button
            onClick={runAll}
            disabled={running}
            data-testid="run-checks-btn"
            className="h-10"
          >
            {running ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Run all checks
          </Button>
        </div>

        <div className="space-y-2" data-testid="checks-list">
          {CHECKS.map((c) => {
            const r = results[c.id];
            const status = r?.status;
            return (
              <div
                key={c.id}
                data-testid={`check-${c.id}`}
                className="flex items-start gap-3 p-4 rounded-xl border border-border"
              >
                <div className="w-6 shrink-0 mt-0.5">
                  {status === "running" && (
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  )}
                  {status === "pass" && (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  )}
                  {status === "fail" && (
                    <XCircle className="w-5 h-5 text-destructive" />
                  )}
                  {!status && (
                    <span className="block w-2.5 h-2.5 rounded-full bg-muted-foreground/30 mt-1.5 ml-1.5" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <span className="mono text-sm font-bold">{c.label}</span>
                    {status && (
                      <span
                        className={`text-xs font-bold uppercase tracking-wider ${
                          status === "pass"
                            ? "text-emerald-600"
                            : status === "fail"
                            ? "text-destructive"
                            : "text-muted-foreground"
                        }`}
                      >
                        {status}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {c.desc}
                  </div>
                  {r?.note && (
                    <div
                      className={`text-xs mt-1 mono ${
                        status === "pass"
                          ? "text-emerald-700"
                          : status === "fail"
                          ? "text-destructive/90"
                          : ""
                      }`}
                    >
                      {r.note}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
